// File: src/pages/DeeperNotFaster.js
import React, { useMemo, useState } from "react";
import { Helmet } from "react-helmet";
import {
  clusters,
  conviction,
  corrections,
  heldUp,
  keywordBank,
  methodNotes,
  modeMeaning,
  namedIndustries,
  taxonomy,
  verdictMeaning,
  VERDICTS,
} from "../data/deeperNotFaster";
import "./DeeperNotFaster.css";

const MODES = ["deeper", "faster", "mixed"];

function Badge({ kind, value, title }) {
  return (
    <span className={`dnf-badge dnf-${kind} dnf-${value}`} title={title}>
      {value}
    </span>
  );
}

function Row({ row }) {
  return (
    <article className="dnf-card">
      <header className="dnf-card-head">
        <span className="dnf-num">{row.n}</span>
        <h4>{row.name}</h4>
      </header>

      <div className="dnf-tags">
        <Badge kind="verdict" value={row.verdict} title={verdictMeaning[row.verdict]} />
        <Badge kind="mode" value={row.mode} title={modeMeaning[row.mode]} />
        <span className="dnf-path" title={row.pathNote || ""}>
          path {row.path}
          {row.pathNote ? ` (${row.pathNote})` : ""}
        </span>
      </div>

      {(row.verdictNote || row.modeNote) && (
        <p className="dnf-note">
          {row.verdictNote && <span>{row.verdictNote}</span>}
          {row.verdictNote && row.modeNote && <span className="dnf-sep">·</span>}
          {row.modeNote && <span>{row.modeNote}</span>}
        </p>
      )}

      <dl className="dnf-fields">
        <dt>The evaluator</dt>
        <dd>{row.evaluator}</dd>
        <dt>Where it lives</dt>
        <dd>{row.where}</dd>
        <dt>Anchor evidence</dt>
        <dd>{row.evidence}</dd>
      </dl>
    </article>
  );
}

export default function DeeperNotFaster() {
  const [verdictFilter, setVerdictFilter] = useState(null);
  const [modeFilter, setModeFilter] = useState(null);

  const counts = useMemo(() => {
    const c = { verdict: {}, mode: {}, total: 0 };
    clusters.forEach((cl) =>
      cl.rows.forEach((r) => {
        c.verdict[r.verdict] = (c.verdict[r.verdict] || 0) + 1;
        c.mode[r.mode] = (c.mode[r.mode] || 0) + 1;
        c.total += 1;
      })
    );
    return c;
  }, []);

  const shown = useMemo(
    () =>
      clusters
        .map((cl) => ({
          ...cl,
          rows: cl.rows.filter(
            (r) =>
              (!verdictFilter || r.verdict === verdictFilter) &&
              (!modeFilter || r.mode === modeFilter)
          ),
        }))
        .filter((cl) => cl.rows.length),
    [verdictFilter, modeFilter]
  );

  const shownCount = shown.reduce((n, cl) => n + cl.rows.length, 0);

  return (
    <div className="dnf-page">
      <Helmet>
        <title>Niche agent industries: deeper, not faster | Helia Jamshidi</title>
        <meta
          name="description"
          content="Forty-six agentic AI sub-industries, sorted by whether anything real settles the score: serious, emerging, or fantasy. Research from August 2026."
        />
      </Helmet>

      <div className="ai-report-banner">
        <strong>AI-researched report</strong>
        <span>Worth sharing, with that caveat.</span>
      </div>
      <h1>Niche agent industries: deeper, not faster</h1>
      <p className="dnf-date">A map of 46 agentic AI sub-industries · researched 31 August 2026</p>

      <section className="dnf-intro">
        <p>
          Most of the agentic AI I come across is about doing the same work faster. Summarise
          eight hundred contracts, draft the memo, review the pull request, write the boilerplate.
          Slightly worse than a good person, much cheaper, available at three in the morning. That
          is a real business. It is just not the one I find interesting.
        </p>
        <p>
          The other kind is what I went looking for: companies where agents do something that was
          genuinely not possible before. Not a human task compressed, but a search space opened.
          Generate ten thousand plausible mechanisms, run the tests, learn from the failures,
          propose the next batch. Try the hundred thousand ugly hypotheses that no human would ever
          have the patience for. Deeper, rather than faster.
        </p>
        <p>
          This page is the list that came back. Forty-six sub-industries across eight clusters,
          each with a verdict: is this real, is it early, or is it a story running ahead of its
          evidence. Some of it is obvious once you see it. Some of it surprised me, mostly in the
          direction of disappointment.
        </p>
        <p>
          One question did nearly all of the sorting: <strong>who settles the score, and how
          fast?</strong> If a power market, a prover, a compiler, a wet lab or a robot arm tells
          you within days whether the agent was right, the field tends to be real. If the only
          judge is a panel of humans, or a model of the world that nobody has checked against the
          world, it tends not to be. That single test explains almost every verdict below.
        </p>
        <p className="dnf-provenance">
          How this was made, since it matters for how much you should trust it: eight research
          agents ran in parallel on 31 August 2026, roughly four hundred web calls between them.
          Every claim carries a confidence tag. <strong>HIGH</strong> means a primary page was
          fetched that day: a company's own site, its own job board, arXiv, a regulator.{" "}
          <strong>MEDIUM</strong> means one reliable secondary source. <strong>LOW</strong> means
          memory only. <strong>UNVERIFIED</strong> means nobody could reach it, and it is left
          marked rather than guessed at. Funding numbers and locations from aggregator sites were
          caught being wrong three separate times, so only a company's own pages count here.
        </p>
      </section>

      <section className="dnf-legend">
        <div className="dnf-legend-row">
          <span className="dnf-legend-label">Verdict</span>
          {VERDICTS.map((v) => (
            <span key={v} className="dnf-legend-item">
              <span className={`dnf-badge dnf-verdict dnf-${v}`}>{v}</span>
              <span className="dnf-legend-text">{verdictMeaning[v]}</span>
            </span>
          ))}
        </div>
        <div className="dnf-legend-row">
          <span className="dnf-legend-label">Kind</span>
          {MODES.map((m) => (
            <span key={m} className="dnf-legend-item">
              <span className={`dnf-badge dnf-mode dnf-${m}`}>{m}</span>
              <span className="dnf-legend-text">{modeMeaning[m]}</span>
            </span>
          ))}
        </div>
        <div className="dnf-legend-row">
          <span className="dnf-legend-label">Path</span>
          <span className="dnf-legend-item">
            <span className="dnf-path">path A</span>
            <span className="dnf-legend-text">Applied agentic. Many customers, doable today.</span>
          </span>
          <span className="dnf-legend-item">
            <span className="dnf-path">path B</span>
            <span className="dnf-legend-text">Deep tech. Few niche customers, long payoff.</span>
          </span>
        </div>
      </section>

      <h2>The scoreboard</h2>

      <div className="dnf-filters">
        <div className="dnf-filter-group">
          <span className="dnf-filter-label">Verdict</span>
          <button
            className={!verdictFilter ? "active" : ""}
            onClick={() => setVerdictFilter(null)}
          >
            all {counts.total}
          </button>
          {VERDICTS.map((v) => (
            <button
              key={v}
              className={verdictFilter === v ? "active" : ""}
              onClick={() => setVerdictFilter(verdictFilter === v ? null : v)}
            >
              {v.toLowerCase()} {counts.verdict[v] || 0}
            </button>
          ))}
        </div>
        <div className="dnf-filter-group">
          <span className="dnf-filter-label">Kind</span>
          <button className={!modeFilter ? "active" : ""} onClick={() => setModeFilter(null)}>
            all
          </button>
          {MODES.map((m) => (
            <button
              key={m}
              className={modeFilter === m ? "active" : ""}
              onClick={() => setModeFilter(modeFilter === m ? null : m)}
            >
              {m} {counts.mode[m] || 0}
            </button>
          ))}
        </div>
      </div>

      {(verdictFilter || modeFilter) && (
        <p className="dnf-count">
          Showing {shownCount} of {counts.total}.{" "}
          <button
            className="dnf-clear"
            onClick={() => {
              setVerdictFilter(null);
              setModeFilter(null);
            }}
          >
            clear filters
          </button>
        </p>
      )}

      {shown.map((cl) => (
        <section key={cl.id} className="dnf-cluster">
          <h3>{cl.title}</h3>
          <p className="dnf-digest">{cl.digest}</p>
          <div className="dnf-grid">
            {cl.rows.map((r) => (
              <Row key={r.n} row={r} />
            ))}
          </div>
        </section>
      ))}

      {!shown.length && <p className="dnf-empty">Nothing matches that combination.</p>}

      <h2>The pattern underneath</h2>
      <p>
        Across all forty-six rows, the same three tests decided the verdict. They work on any new
        company you come across, and they take about a minute each.
      </p>
      {conviction.map((t) => (
        <div key={t.title} className="dnf-test">
          <h3>{t.title}</h3>
          <ul>
            {t.points.map((p) => (
              <li key={p.lead}>
                <strong>{p.lead}</strong>
                {p.arrow && <span className="dnf-arrow">{p.arrow}</span>}
                {p.arrow ? " " : ": "}
                {p.body}
              </li>
            ))}
          </ul>
        </div>
      ))}

      <p className="dnf-punchline">
        The uncomfortable finding, if you are looking for somewhere to work rather than somewhere
        to invest: the deeper work and the reachable job are nearly disjoint sets. Deep work sits
        in laboratories that hire researchers, or in hardware companies that hire on site. And the
        2025 to 2026 cohort is raising ten to fifty times more than the first generation of
        "AI plus lab" companies that mostly stalled or died, on the same thesis plus LLM agents.
        Either the agents are the missing ingredient, or this is a bigger bubble. The evidence does
        not yet settle it.
      </p>

      <h2>The four I started with</h2>
      <p>
        Before the search I named four industries off the top of my head, because they are the ones
        I actually care about. Here is what the evidence said about each.
      </p>
      {namedIndustries.map((n) => (
        <div key={n.title} className="dnf-named">
          <h3>{n.title}</h3>
          <p>{n.body}</p>
        </div>
      ))}

      <h2>Where "deeper" actually happens</h2>
      <p>
        A cleaner way to think about the whole map. Agents as high-dimensional search and
        intervention engines over people, learning paths, cities, markets, organisations, legal
        systems and media ecosystems. Five shapes, and everything above is one of them.
      </p>
      <div className="dnf-taxonomy">
        {taxonomy.map((t) => (
          <div key={t.letter} className="dnf-tax-item">
            <span className="dnf-tax-letter">{t.letter}</span>
            <div>
              <strong>{t.title}</strong>
              <p>{t.body}</p>
            </div>
          </div>
        ))}
      </div>
      <p className="dnf-punchline">
        The boring AI company sells "fewer employees". The interesting AI company sells "more
        explored reality".
      </p>

      <h2>Things that turned out to be wrong</h2>
      <p>
        The starting list for this search came out of an ordinary chat with a model, and a good
        chunk of it did not survive contact with the primary sources. Keeping the corrections
        visible seems more useful than quietly deleting them, partly because the failure modes
        repeat: acquisitions treated as independent companies, benchmarks reported as product
        achievements, funding rounds that exist only on aggregator sites.
      </p>
      <div className="dnf-table-wrap">
        <table className="dnf-table">
          <thead>
            <tr>
              <th>The claim</th>
              <th>What it actually is</th>
              <th>Confidence</th>
            </tr>
          </thead>
          <tbody>
            {corrections.map((c) => (
              <tr key={c.claim}>
                <td>{c.claim}</td>
                <td>{c.fix}</td>
                <td>
                  <span className={`dnf-conf dnf-conf-${c.conf}`}>{c.conf}</span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p>
        <strong>What held up:</strong> {heldUp}
      </p>

      <h2>Notes on method</h2>
      <ul className="dnf-method">
        {methodNotes.map((m) => (
          <li key={m}>{m}</li>
        ))}
      </ul>

      <details className="dnf-details">
        <summary>The keyword bank, if you want to run this search yourself</summary>
        <p>
          Around 250 keywords were generated across the eight passes. These are the productive and
          the deliberately strange ones, so that the next sweep starts somewhere wider than "AI
          agents startup". The German-language terms mattered more than expected: several companies
          are invisible in English search.
        </p>
        {keywordBank.map((k) => (
          <div key={k.cluster} className="dnf-keywords">
            <strong>{k.cluster}</strong>
            <p>{k.keywords}</p>
          </div>
        ))}
      </details>
    </div>
  );
}
